import React, { useState } from "react";
import {
  useOrder,
  useOrderSearch,
} from "@ludens-reklame/rubics-v3-react/order/hooks";
import { Order as IOrder } from "@ludens-reklame/rubics-v3-sdk/order/types";
import { Busy } from "../../../internal-components/Busy.js";
import {
  Card,
  IconLink,
  MobileOrders,
  StyledOrderTable,
  Sum,
} from "../styles.js";
import { formatCurrency, formatDate } from "../../../util/formatter.js";
import { Button } from "../../../internal-components/Button.js";
import { chevron } from "../../../icons/index.js";
import { Input } from "../../../internal-components/Input/Input.js";
import { DefinitionList } from "../../../internal-components/DefinitionList.js";
import { EmptyState } from "../../../internal-components/EmptyState.js";

export const Orders: React.FC = () => {
  const [orderId, setOrderId] = useState<string>("");
  const orderSearch = useOrderSearch({ fetchOnInit: true });

  if (orderId) {
    return <Order orderId={orderId} />;
  }

  return (
    <Busy $busy={orderSearch.loading}>
      <MobileOrders className="mobile">
        {orderSearch.results.length > 0 ? (
          <>
            {orderSearch.results.map((order) => (
              <a
                key={order._id}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setOrderId(order._id);
                }}
                aria-label={`Gå til ordre ${order.number}`}
              >
                <h3 className="h5">Ordrenr: {order.number}</h3>
                <DefinitionList $horizontal>
                  <dt>Bestilt</dt>
                  <dd>{formatDate(order.created)}</dd>
                  <dt>Status</dt>
                  <dd>{order.status}</dd>
                  <dt>Totalbeløp</dt>
                  <dd>
                    <b>{formatCurrency(order.total + order.totalTax)}</b>
                  </dd>
                </DefinitionList>
                <IconLink as="i">{chevron}</IconLink>
              </a>
            ))}
            {orderSearch.hasMore && (
              <Button
                $variant="secondary"
                onClick={() => {
                  orderSearch.search(true);
                }}
              >
                {orderSearch.loading ? "Laster…" : "Last inn flere"}
              </Button>
            )}
          </>
        ) : (
          <EmptyState
            title="Ingen ordrer"
            text="Du vil se dine ordrer her etter at du har gjennomført et kjøp"
          />
        )}
      </MobileOrders>
      <Card className="desktop">
        <h2 className="h3">Dine ordrer</h2>

        {orderSearch.results.length > 0 ? (
          <StyledOrderTable $iconTail>
            <thead className="h5">
              <tr>
                <th>Ordrenummer</th>
                <th>Bestilt</th>
                <th>Status</th>
                <th>Totalbeløp</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orderSearch.results.map((order) => (
                <tr key={order._id}>
                  <td>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setOrderId(order._id);
                      }}
                      aria-label={`Gå til ordre ${order.number}`}
                    >
                      #{order.number}
                    </a>
                  </td>
                  <td>{formatDate(order.created)}</td>
                  <td>{order.status}</td>
                  <td className="h5">
                    {formatCurrency(order.total + order.totalTax)}
                  </td>
                  <td>
                    <IconLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setOrderId(order._id);
                      }}
                      aria-label={`Gå til ordre ${order.number}`}
                    >
                      {chevron}
                    </IconLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledOrderTable>
        ) : (
          <EmptyState
            title="Ingen ordrer"
            text="Du vil se dine ordrer her etter at du har gjennomført et kjøp"
          />
        )}

        {orderSearch.hasMore && (
          <Button
            $variant="secondary"
            onClick={() => {
              orderSearch.search(true);
            }}
          >
            {orderSearch.loading ? "Laster…" : "Last inn flere"}
          </Button>
        )}
      </Card>
    </Busy>
  );
};

export const Order: React.FC<{ orderId: string }> = ({ orderId }) => {
  const [addingComment, setAddingComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const orderInterface = useOrder(orderId);
  const order = orderInterface.order;

  if (!order) {
    return null;
  }

  return (
    <>
      <Card className="order-card">
        <h2 className="h3">Ordre #{order.number}</h2>
        <p>Status: {order.status}</p>
        <OrderTable order={order} />
      </Card>
      <Card>
        <h4>Kommentarer</h4>
        {addingComment ? (
          <Busy $busy={orderInterface.loading}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();

                await orderInterface.createNote(comment);

                setAddingComment(false);
                setComment("");
              }}
            >
              <Input
                label="Kommentar"
                textarea
                autoFocus
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button
                type="submit"
                $variant="secondary"
                disabled={orderInterface.loading}
              >
                {orderInterface.loading ? "Laster…" : "Send inn"}
              </Button>
            </form>
          </Busy>
        ) : (
          <>
            {order.notes.length === 0 ? (
              <p className="b2">Ingen kommentarer enda</p>
            ) : (
              <ul>
                {order.notes.map((n) => (
                  <li key={n._id}>{n.content}</li>
                ))}
              </ul>
            )}
            <Button $variant="secondary" onClick={() => setAddingComment(true)}>
              Ny kommentar
            </Button>
          </>
        )}
      </Card>
    </>
  );
};

export const OrderTable: React.FC<{ order: IOrder }> = ({ order }) => {
  return (
    <StyledOrderTable>
      <thead className="h5">
        <tr>
          <th>Produkt</th>
          <th>Antall</th>
          <th>Pris</th>
        </tr>
      </thead>
      <tbody>
        {order.lineItems.map((item) => (
          <tr key={item._id}>
            <td className={item.image ? "image-td" : ""}>
              {item.image && (
                <img
                  src={item.image.url}
                  alt={item.image.altText || item.name}
                />
              )}

              {item.name}
              {item.attributes.map((a) => (
                <p key={a._id}>{a.value}</p>
              ))}
            </td>
            <td>{item.quantityText}</td>
            <td className="h5">{formatCurrency(item.total + item.totalTax)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3}>
            <Sum>
              <div>
                <p>Delsum</p>
                <p>MVA</p>
                <p className="h5">Totalt</p>
              </div>
              <div>
                <p>{order.totalText}</p>
                <p>{order.totalTaxText}</p>
                <p className="h5">
                  {formatCurrency(order.total + order.totalTax)}
                </p>
              </div>
            </Sum>
          </td>
        </tr>
      </tfoot>
    </StyledOrderTable>
  );
};
